import React from 'react';

export default function AttendanceSummaryCards({ summary, student, dateRange }) {
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'present': return 'from-green-500 to-green-600';
      case 'absent': return 'from-red-500 to-red-600';
      case 'late': return 'from-yellow-500 to-yellow-600';
      case 'excused': return 'from-blue-500 to-blue-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'present':
        return (
          <svg className="w-8 h-8 text-green-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
        );
      case 'absent':
        return (
          <svg className="w-8 h-8 text-red-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'late':
        return (
          <svg className="w-8 h-8 text-yellow-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-8 h-8 text-gray-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
    }
  };

  if (!summary || !student) {
    return null;
  }

  return (
    <div className="mb-6">
      {/* Summary Cards - All 5 cards in a single row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        {/* Total Records */}
        <div className="bg-gradient-to-r from-slate-500 to-slate-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-100 text-sm font-medium">Total Records</p>
              <p className="text-3xl font-bold">{summary.totalRecords}</p>
            </div>
            <div className="bg-slate-400 bg-opacity-30 rounded-lg p-3">
              <svg className="w-8 h-8 text-slate-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Status Cards - Dynamic based on available statuses */}
        {Object.entries(summary.statusCounts).map(([status, count]) => (
          <div key={status} className={`bg-gradient-to-r ${getStatusColor(status)} rounded-xl p-6 text-white`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium opacity-90`}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </p>
                <p className="text-3xl font-bold">{count}</p>
                <p className="text-sm opacity-90">
                  {summary.totalRecords > 0 ? ((count / summary.totalRecords) * 100).toFixed(1) : 0}%
                </p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-3">
                {getStatusIcon(status)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Additional Stats */}
      {(summary.earlyDepartureCount > 0 || summary.totalLateMinutes > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {summary.earlyDepartureCount > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-8 w-8 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-900">Early Departures</p>
                  <p className="text-lg font-semibold text-purple-600">{summary.earlyDepartureCount}</p>
                </div>
              </div>
            </div>
          )}

          {summary.totalLateMinutes > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-900">Total Late Minutes</p>
                  <p className="text-lg font-semibold text-red-600">{summary.totalLateMinutes}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
